import { UseFormReturn } from "react-hook-form";
import Select from "@/components/Select";
import Button from "@/components/Button";
import MultiSelect from "@/components/MultiSelect";
import { jobSearchStatuses, minimumCompensationOptions } from "../_constants";
import { LOCATION_PREFERENCES } from "@/lib/usStateHelpers";
import { Controller } from "react-hook-form";
import { JobPreferencesFormData } from "../job-preferences";
import classNames from "classnames";

interface EditPreferencesCardProps {
  form: UseFormReturn<JobPreferencesFormData>;
  onSubmit: (formData: JobPreferencesFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export default function EditPreferencesCard({ form, onSubmit, onCancel, isSubmitting }: EditPreferencesCardProps) {
  return (
    <div className="space-y-6 w-full">
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="jobSearchStatus"
          render={({ field }) => (
            <Select
              label="Job Search Status"
              options={jobSearchStatuses}
              value={field.value}
              onSelect={(value: string) => field.onChange(value)}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="optedInToJobSeekerEmails"
          render={({ field }) => (
            <Select
              label="Email Notifications"
              options={[
                { label: "Opt in", value: "opted_in" },
                { label: "Opt out", value: "opted_out" }
              ]}
              value={field.value ? "opted_in" : "opted_out"}
              onSelect={(value: string) => field.onChange(value === "opted_in")}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="jobCompensationMinimum"
          render={({ field }) => (
            <Select
              label="Compensation Minimum"
              options={minimumCompensationOptions}
              value={field.value?.toString() || "0"}
              onSelect={(value: string) => field.onChange(value)}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="openToSeasonJobs"
          render={({ field }) => (
            <Select
              label="Open to Seasonal Positions"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" }
              ]}
              value={field.value ? "yes" : "no"}
              onSelect={(value: string) => field.onChange(value === "yes")}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="locationPreferences"
          render={({ field }) => (
            <MultiSelect
              label="Location"
              options={LOCATION_PREFERENCES.map((loc: string) => ({ label: loc, value: loc }))}
              selectedValues={field.value}
              onSelect={(value: string, checked: boolean) => {
                const currentLocations = field.value || [];
                const newLocations = checked
                  ? [...currentLocations, value]
                  : currentLocations.filter((l: string) => l !== value);
                field.onChange(newLocations);
              }}
              onClear={() => field.onChange([])}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="interestInInternationalPositions"
          render={({ field }) => (
            <Select
              label="Interested in International Positions"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" }
              ]}
              value={field.value ? "yes" : "no"}
              onSelect={(value: string) => field.onChange(value === "yes")}
            />
          )}
        />
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outlined" disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          iconClassName={classNames(isSubmitting && "animate-spin w-6 h-6 !block")}
          disabled={isSubmitting}
          icon={isSubmitting ? "progress_activity" : undefined}
          onClick={() => onSubmit(form.getValues())}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
